import { asyncCallback } from "../utils/analysis-async";
import { StepProcess } from "../interfaces/stepProcess";
import { DomainDTO, LinkDisplayedAsTypes, LinksWithStyles, NodeData, ResultInfo } from "../models/shared.model";
import { MailUtils } from "../utils/mail.utils";
import { StepResult } from "../models/step-result";
import { StringUtils } from "../utils/string.utils";
import { IAnalyzerConfig } from "../interfaces/analyzer-config";
import { ANALYZERS } from "../models/constants";


export class EmailHasLinks extends StepProcess {
  process = async (data?: NodeData): Promise<any> => {
    let links: LinksWithStyles[] = await this.linksService.getLinks(this.run);

    let res = false;
    let count = 0;
    if (links != null && links.length > 0) {
      res = true;
      count = links.length;
    }

    return asyncCallback<any>(async (resolve) => {
      resolve(new StepResult(res, this.translate.instant('links_flow.email_has_links', {count})));
    });
  };
}

export class IsLinkDeceptive extends StepProcess {
  process = async (data?: NodeData, info?: ResultInfo): Promise<any> => {
    return asyncCallback<any>(async (resolve) => {
      let result = false

      const deceptive = this.linksService.isLinkDeceptive(data.link.getAttribute('href'));

      let reasonLog = this.translate.instant("links_flow.is_deceptive_false");

      if(deceptive){
        reasonLog = this.translate.instant('links_flow.is_deceptive_true', {url: deceptive});
        result = true;
        info = {...info, deceptiveLink: deceptive, isConcerning: true}
      }

      resolve(new StepResult(result, reasonLog, info));
    });
  };
}

export class IsHTMLConditionalLink extends StepProcess {
  process = async (data?: NodeData, info?: ResultInfo): Promise<any> => {
    let link = data.link;

    return asyncCallback<any>(async (resolve) => {
      let res = false;

      const isConditionalLink = link?.getAttribute("is-conditional-mso");
      if (isConditionalLink != null && isConditionalLink == "true") {
        res = true;
        if (info.isConcerning == undefined) {
          info = { ...info, isConcerning: res };
        }
      }

      const logReason = this.translate.instant(res ? 'links_flow.link_is_conditional' : 'links_flow.link_is_not_conditional')

      resolve(new StepResult(res, logReason, { ...info, linkIsConditional: res }));
    });
  };
}

export class AreLinksSecure extends StepProcess {
  process = async (data?: NodeData, info?: ResultInfo): Promise<any> => {
    let linkInfo = this.checkViewedAs(data.link);

    return asyncCallback<any>(async (resolve, reject) => {
      try {
        const href = info.deceptiveLink ?? data.link?.getAttribute('href');

        if (href && !href.startsWith("https://")) {
          if (info.isConcerning == undefined) {
            info = { ...info, isConcerning: true };
          }
          resolve(new StepResult(false, this.translate.instant('links_flow.are_links_secure_http'),{ ...info, ...linkInfo, linkSecure: false }));
        } else
          resolve(new StepResult(true, this.translate.instant('links_flow.are_links_secure'),{ ...info, ...linkInfo, linkSecure: true }));
      } catch (e) {
        console.log(this.translate.instant('links_flow.error_in_are_links_secure', { error: e }));
        reject(e);
      }
    });
  };

  private checkViewedAs(link: HTMLAnchorElement) {
    let displayedAs: LinkDisplayedAsTypes | string = LinkDisplayedAsTypes.TEXT;

    // Check if the link contain text
    if (link.text.trim()) {
      let res = this.linksService.ifLinkIsDisplayedInText(link);
      if (res)
        displayedAs = LinkDisplayedAsTypes.FULL_URL;
      else
        displayedAs = `${link.text}`;
      return { displayedAs: displayedAs };
    }

    if (link.hasAttribute('data-analysis-styles-unavailable'))
      return { displayedAs: this.translate.instant('analysis.style_unavailable') };

    // Check for any styles or classes acting as an image
    const linkImg = this.isLinkActingAsImg(link);
    if (linkImg)
      return { displayedAs: LinkDisplayedAsTypes.IMAGE, linkImageUrl: linkImg };

    // Check for any styles or classes acting as a button
    if (this.isLinkActingAsButton(link))
      return { displayedAs: LinkDisplayedAsTypes.BUTTON };

    return { displayedAs: LinkDisplayedAsTypes.OTHERS };
  }

  isLinkActingAsButton(link: HTMLAnchorElement) {
    const buttonClasses = ["btn", "button"];
    if (buttonClasses.some(cls => link.outerHTML.includes(cls))) {
      return true;
    }

    // Check for pointer cursor inline styles
    if (link.hasAttribute('data-analysis-styles-unavailable')) return false;
    return link.getAttribute('data-analysis-cursor') === 'pointer';
  }

  isLinkActingAsImg(link: HTMLAnchorElement) {
    if(!link || !link.outerHTML)
      return false;
    const imgClasses = ["img", "image", "icon"];
    if (imgClasses.some(cls => link.outerHTML.includes(cls))) {
      // extract imgUrl to be displayed in ui
      const regex = /<img[^>]+src=["']([^"']+)["']/;
      const match = link.outerHTML.match(regex);

      return match?.[1] || false;
    }
    return false;
  }

}

export class IsDestinationTrustedDomain extends StepProcess {
  process = async (data?: NodeData, info?: ResultInfo): Promise<any> => {
    return asyncCallback<any>(async (resolve, reject) => {
      try {
        let trustedDomains: DomainDTO[] = this.domainService.getDomains();
        const href = info.deceptiveLink ?? data.link?.getAttribute('href');

        if (href) {
          const linkDomain: string = MailUtils.parseMultiLevelDomain(href);

          let res = trustedDomains.some(value =>
            StringUtils.compareCaseInsensitive(value.domain, linkDomain));
          if(res){
            const domain = trustedDomains.find(value =>
              StringUtils.compareCaseInsensitive(value.domain, linkDomain));

            info = this.checkLinkDomainAge(domain.register_dt, info)
          }

          if(!res){
            const personalTrusted = this.domainService.loadPersonalTrustedListFromCache();
            res = personalTrusted.domains.has(linkDomain?.toLowerCase());
            if(res) {
              const domainDate = await this.domainService.getDomainRegisteredDate(linkDomain?.toLowerCase());
              info = this.checkLinkDomainAge(domainDate, info)
            }
          }

          if (info.isConcerning == undefined) {
            info = { ...info, isConcerning: !res };
          }
          let reasonLog = this.translate.instant(
            res ? 'links_flow.trusted_domain' : 'links_flow.untrusted_domain',
            { domain: linkDomain }
          );

          resolve(new StepResult(res, reasonLog, { ...info, linkDomainTrusted: res }));
        }
        resolve(new StepResult(true, this.translate.instant('links_flow.link_does_not_have_url'),
          { ...info, linkDomainTrusted: true }));
      } catch (e) {
        console.log(this.translate.instant('links_flow.error_in_destination_trusted_domain', { error: e }));
        reject(e);
      }
    });
  };

  private checkLinkDomainAge(linkDate: Date, info: ResultInfo){
    const config: IAnalyzerConfig = this.configService.getAnalyzerConfiguration(ANALYZERS.DOMAIN_AGE);

    const domainAge = this.domainService.calculateDomainAge(linkDate);

    let linkDomainAge = domainAge.formattedDate;
    let linkIsDomainNew = domainAge.months <= config.thresholds.high;
    return {...info, domainAge: linkDomainAge, isDomainNew: linkIsDomainNew};
  }
}

export class IsClearAndDirectLink extends StepProcess {
  process = async (data?: NodeData, info?: ResultInfo): Promise<any> => {

    return asyncCallback<any>(async (resolve, reject) => {
      try {
        let res = this.linksService.ifLinkIsDisplayedInText(data.link);

        if (info.isConcerning == undefined) {
          info = { ...info, isConcerning: !res };
        }
        let reasonLog = res ? this.translate.instant('links_flow.link_url_displayed') :
            this.translate.instant('links_flow.link_url_not_displayed');
        resolve(new StepResult(res, reasonLog,{ ...info, linkClearAndDirect: res }));
      } catch (e) {
        console.log(this.translate.instant('links_flow.error_in_is_clear_and_direct_link', { error: e }));
        reject(e);
      }
    });
  };
}
